import { useState } from 'react'
import type { Group, CreateGroupInput, UpdateGroupInput } from '../model/contracts'
import { GroupForm } from './GroupForm'
import { Button } from '../../../shared/ui/Button'

interface GroupListProps {
  groups: Group[]
  availableSources: Array<{ id: string; name: string }>
  availablePages: Array<{ id: string; name: string }>
  onCreate: (data: CreateGroupInput) => void
  onUpdate: (data: UpdateGroupInput) => void
  onDelete: (id: string) => void
}

export function GroupList({
  groups,
  availableSources,
  availablePages,
  onCreate,
  onUpdate,
  onDelete,
}: GroupListProps) {
  const [editingId, setEditingId] = useState<string | null>(null)
  const [showForm, setShowForm] = useState(false)

  const handleEdit = (group: Group) => {
    setEditingId(group.id)
    setShowForm(false)
  }

  const handleCancel = () => {
    setEditingId(null)
    setShowForm(false)
  }

  const handleSubmit = (data: CreateGroupInput | UpdateGroupInput) => {
    if ('id' in data) {
      onUpdate(data)
      setEditingId(null)
    } else {
      onCreate(data as CreateGroupInput)
      setShowForm(false)
    }
  }

  const editingGroup = editingId ? groups.find((g) => g.id === editingId) : undefined

  const getSourceNames = (sourceIds: string[]) => {
    return sourceIds
      .map((id) => availableSources.find((a) => a.id === id)?.name)
      .filter(Boolean)
      .join(', ')
  }

  const getPageNames = (pageIds: string[]) => {
    return pageIds
      .map((id) => availablePages.find((p) => p.id === id)?.name)
      .filter(Boolean)
      .join(', ')
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
      <div
        style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
      >
        <h2 style={{ margin: 0, fontSize: 24, fontWeight: 600 }}>Monitoring Groups</h2>
        {!showForm && !editingId && (
          <Button onClick={() => setShowForm(true)}>Add Group</Button>
        )}
      </div>

      {(showForm || editingGroup) && (
        <div
          style={{
            padding: 24,
            borderRadius: 12,
            border: '1px solid var(--panel-border)',
            background: 'var(--panel)',
          }}
        >
          <GroupForm
            group={editingGroup}
            availableSources={availableSources}
            availablePages={availablePages}
            onSubmit={handleSubmit}
            onCancel={handleCancel}
          />
        </div>
      )}

      {groups.length === 0 ? (
        <div
          style={{
            padding: 48,
            textAlign: 'center',
            borderRadius: 12,
            border: '1px solid var(--panel-border)',
            background: 'var(--panel)',
            color: 'var(--muted)',
          }}
        >
          <div style={{ fontSize: 16, marginBottom: 8 }}>No groups yet</div>
          <div style={{ fontSize: 14 }}>Create your first monitoring group</div>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {groups.map((group) => (
            <div
              key={group.id}
              style={{
                padding: 20,
                borderRadius: 12,
                border: '1px solid var(--panel-border)',
                background: 'var(--panel)',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-start',
                gap: 16,
              }}
            >
              <div style={{ flex: 1 }}>
                <h3 style={{ margin: 0, fontSize: 18, fontWeight: 600, marginBottom: 8 }}>
                  {group.name}
                </h3>
                {group.description && (
                  <div style={{ fontSize: 14, color: 'var(--muted)', marginBottom: 12 }}>
                    {group.description}
                  </div>
                )}
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 8,
                    fontSize: 14,
                  }}
                >
                  {group.sourceIds.length > 0 && (
                    <div>
                      <strong style={{ color: 'var(--muted)' }}>Sources:</strong>{' '}
                      <span style={{ color: 'var(--text)' }}>
                        {getSourceNames(group.sourceIds) || 'None'}
                      </span>
                    </div>
                  )}
                  {group.pageIds.length > 0 && (
                    <div>
                      <strong style={{ color: 'var(--muted)' }}>Pages:</strong>{' '}
                      <span style={{ color: 'var(--text)' }}>
                        {getPageNames(group.pageIds) || 'None'}
                      </span>
                    </div>
                  )}
                  {group.sourceIds.length === 0 && group.pageIds.length === 0 && (
                    <div style={{ color: 'var(--muted)', fontSize: 13 }}>
                      No sources or pages assigned
                    </div>
                  )}
                </div>
                <div
                  style={{
                    marginTop: 12,
                    fontSize: 12,
                    color: 'var(--muted)',
                    opacity: 0.7,
                  }}
                >
                  Created {new Date(group.createdAt).toLocaleDateString()}
                </div>
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <Button variant="neutral" onClick={() => handleEdit(group)}>
                  Edit
                </Button>
                <Button
                  variant="danger"
                  onClick={() => {
                    if (confirm(`Delete group "${group.name}"?`)) {
                      onDelete(group.id)
                    }
                  }}
                >
                  Delete
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
